

import { Modal as AntdModal, Button } from 'antd';
import {
    useEffect,
    useState
} from 'react';

import { readClipboard, writeClipboard } from './clipboard';

export default function Externals({
    comps,
    setComps,
    setView,
    pages,
    setPages,
    vars,
    setVars,
    externals,
    setExternals
}) {

    // 'Flags' = list view (the menu opens straight here), 'Edit Flag' = editor.
    const [modalTitle, setModalTitle] = useState('Flags');
    const [isEditingFlag, setIsEditingFlag] = useState(-1);

    const [flagName, setFlagName] = useState('');

    // Indices of flags ticked for copy. Cleared on any list mutation, since
    // indices shift under duplicate/delete/paste.
    const [selected, setSelected] = useState(() => new Set());
    // Anchor for shift-click range selection: the last individually clicked row.
    const [lastToggled, setLastToggled] = useState(null);
    const [clipboardCount, setClipboardCount] = useState(() => readClipboard('flags').length);

    useEffect(() => {
        if (isEditingFlag >= 0) {
            setFlagName(externals.flags[isEditingFlag].name);
        }
    }, [isEditingFlag])

    // Refresh the paste count when another tab copies (same-tab copies update
    // it directly in copySelected).
    useEffect(() => {
        const onStorage = () => setClipboardCount(readClipboard('flags').length);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    function toggleSelected(k, shiftKey) {
        const next = new Set(selected);
        if (shiftKey && lastToggled !== null && lastToggled !== k) {
            // Extend the anchor's current state (selected or not) over the range.
            const apply = selected.has(lastToggled);
            const lo = Math.min(lastToggled, k);
            const hi = Math.max(lastToggled, k);
            for (let i = lo; i <= hi; i++) {
                if (apply) next.add(i); else next.delete(i);
            }
        } else {
            if (next.has(k)) next.delete(k); else next.add(k);
        }
        setLastToggled(k);
        setSelected(next);
    }

    function clearSelection() {
        setSelected(new Set());
        setLastToggled(null);
    }

    function copySelected() {
        const items = (externals?.flags || []).filter((_, i) => selected.has(i));
        writeClipboard('flags', items);
        setClipboardCount(items.length);
        clearSelection();
    }

    function pasteFlags() {
        const items = readClipboard('flags').filter((item) =>
            item && typeof item.name === 'string' &&
            Array.isArray(item.values) && Array.isArray(item.colors));
        if (items.length === 0) return;
        let new_externals = { ...externals };
        if (new_externals.flags === undefined || new_externals.flags === null) {
            new_externals.flags = [];
        }
        const names = new Set(new_externals.flags.map((f) => f.name));
        for (const item of items) {
            if (names.has(item.name)) {
                let candidate = item.name + ' Copy';
                let n = 2;
                while (names.has(candidate)) {
                    candidate = item.name + ' Copy ' + n;
                    n++;
                }
                item.name = candidate;
            }
            names.add(item.name);
            new_externals.flags.push(item);
        }
        clearSelection();
        setExternals(new_externals);
    }

    // ---- Flag list ---------------------------------------------------------
    let flags_overview = [];
    if (!externals?.flags || externals.flags.length === 0) {
        flags_overview = <div style={{
            padding: '28px 0',
            textAlign: 'center',
            color: 'var(--ed-text-soft)',
            fontWeight: 400
        }}>
            <i className="fa fa-flag-o" style={{ fontSize: 28, display: 'block', marginBottom: 10, opacity: 0.6 }}></i>
            No flags yet — use “Add Flag” below to create one.
        </div>;
    } else {
        for (let k = 0; k < externals.flags.length; ++k) {
            const flag = externals.flags[k];
            const defaultIdx = parseInt(flag.value);
            flags_overview.push(
                <div key={k} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    border: '1px solid var(--ed-border)',
                    borderRadius: 8,
                    marginBottom: 8,
                    background: 'var(--ed-surface-2)'
                }}>
                    <input type='checkbox' title="Select for copy (shift-click to select a range)"
                        checked={selected.has(k)}
                        onClick={(e) => toggleSelected(k, e.shiftKey)}
                        onChange={() => { }}
                        style={{
                            flex: '0 0 auto',
                            width: 15,
                            height: 15,
                            accentColor: 'var(--ed-accent)',
                            cursor: 'pointer'
                        }} />
                    <div style={{ display: 'flex', gap: 4, flex: '0 0 auto' }}>
                        {flag.colors.map((c, ci) => (
                            <span key={ci} style={{
                                width: 14,
                                height: 14,
                                borderRadius: 3,
                                background: c,
                                border: '1px solid rgba(16,24,40,.15)',
                                boxShadow: ci === defaultIdx ? '0 0 0 2px var(--ed-accent)' : 'none'
                            }}></span>
                        ))}
                    </div>
                    <span style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        fontWeight: 500,
                        color: 'var(--ed-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>{flag.name}</span>
                    <a className='panel-button' onClick={(e) => {
                        e.preventDefault();
                        setIsEditingFlag(k);
                        setModalTitle('Edit Flag');
                    }}>
                        <i className="fa fa-pencil" style={{ marginRight: 6 }}></i>Edit
                    </a>
                    <a className='panel-button' onClick={(e) => {
                        e.preventDefault();
                        let new_externals = { ...externals };
                        const copy = JSON.parse(JSON.stringify(flag));
                        const names = new Set(new_externals.flags.map((f) => f.name));
                        let candidate = copy.name + ' Copy';
                        let n = 2;
                        while (names.has(candidate)) {
                            candidate = copy.name + ' Copy ' + n;
                            n++;
                        }
                        copy.name = candidate;
                        new_externals.flags.splice(k + 1, 0, copy);
                        clearSelection();
                        setExternals(new_externals);
                    }}>
                        <i className="fa fa-clone" style={{ marginRight: 6 }}></i>Duplicate
                    </a>
                    <a className='panel-button prop-danger' onClick={(e) => {
                        e.preventDefault();
                        let new_externals = { ...externals };
                        new_externals.flags.splice(k, 1);
                        clearSelection();
                        setExternals(new_externals);
                    }}>Delete</a>
                </div>
            );
        }
    }

    // ---- States of the flag being edited -----------------------------------
    let flag_overview = [];
    if (isEditingFlag >= 0) {
        for (let k = 0; k < externals?.flags[isEditingFlag]?.values?.length; ++k) {
            const isDefault = parseInt(externals.flags[isEditingFlag].value) === k;
            flag_overview.push(
                <div key={k} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 8,
                    marginBottom: 8,
                    border: isDefault ? '1px solid var(--ed-accent)' : '1px solid var(--ed-border)',
                    background: isDefault ? 'var(--ed-accent-soft)' : 'var(--ed-surface-2)'
                }}>
                    <input type='color' className='panel-color-input' style={{
                        width: '2vmax',
                        flex: '0 0 auto'
                    }} onChange={(e) => {
                        let new_externals = { ...externals };
                        new_externals.flags[isEditingFlag].colors[k] = e.target.value;
                        setExternals(new_externals);
                    }} value={externals.flags[isEditingFlag].colors[k]} />
                    <input className='panel-input' value={externals.flags[isEditingFlag].values[k]} style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        fontWeight: isDefault ? 500 : 400,
                        color: isDefault ? 'var(--ed-accent)' : 'var(--ed-text)'
                    }} onChange={(e) => {
                        let new_externals = { ...externals };
                        new_externals.flags[isEditingFlag].values[k] = e.target.value;
                        setExternals(new_externals);
                    }}></input>
                    <i className={isDefault ? 'fa fa-star pointy' : 'fa fa-star-o pointy'}
                        title={isDefault ? 'Default state' : 'Set as default'}
                        style={{
                            flex: '0 0 auto',
                            fontSize: '1.25vmax',
                            color: isDefault ? '#f1a722' : 'var(--ed-text-soft)'
                        }} onClick={(e) => {
                            e.preventDefault();
                            let new_externals = { ...externals };
                            new_externals.flags[isEditingFlag].value = k;
                            setExternals(new_externals);
                        }}></i>
                    <i className="fa fa-trash pointy" title="Delete state" style={{
                        flex: '0 0 auto',
                        fontSize: '1.15vmax',
                        color: 'var(--ed-text-soft)'
                    }} onClick={(e) => {
                        e.preventDefault();
                        if (externals.flags[isEditingFlag]?.values?.length > 1) {
                            let new_externals = { ...externals };
                            new_externals.flags[isEditingFlag].values.splice(k, 1);
                            new_externals.flags[isEditingFlag].colors.splice(k, 1);
                            new_externals.flags[isEditingFlag].value = 0;
                            setExternals(new_externals);
                        }
                    }}></i>
                </div>
            );
        }
    }

    const flag_manager_footer = [
        <Button key="copy" disabled={selected.size === 0}
            title="Copy selected flags (paste here or in another form)"
            onClick={(e) => {
                e.preventDefault();
                copySelected();
            }}>
            Copy{selected.size > 0 ? ' (' + selected.size + ')' : ''}
        </Button>,
        <Button key="paste" disabled={clipboardCount === 0}
            title="Paste copied flags at the end of the list"
            onClick={(e) => {
                e.preventDefault();
                pasteFlags();
            }}>
            Paste{clipboardCount > 0 ? ' (' + clipboardCount + ')' : ''}
        </Button>,
        <Button key="add" type="primary" onClick={(e) => {
            e.preventDefault();
            let new_externals = {
                ...externals
            };
            if (new_externals["flags"] === undefined || new_externals["flags"] === null) {
                new_externals["flags"] = [];
            }
            new_externals.flags.push({
                name: 'New Flag ' + (new_externals.flags.length + 1),
                values: ['Incomplete', 'In Progress', 'Complete'],
                colors: ['#E74C3C', '#F1C40F', '#2ECC71'],
                value: 0
            });
            setExternals(new_externals);
        }}>
            Add Flag
        </Button>,

        <Button key="close" type="primary" danger onClick={(e) => { setView(''); }}>
            Close
        </Button>
    ];

    const edit_flag_footer = [
        <Button key="add" type="primary" onClick={(e) => {
            e.preventDefault();
            let new_externals = { ...externals };
            new_externals.flags[isEditingFlag].values.push('New State');
            new_externals.flags[isEditingFlag].colors.push('#ffffff');
            setExternals(new_externals);
        }}>
            Add State
        </Button>,

        <Button key="back" type="primary" danger onClick={(e) => {
            setModalTitle('Flags');
            let new_externals = { ...externals };
            if (new_externals.flags[isEditingFlag].name === '') {
                new_externals.flags[isEditingFlag].name = flagName;
                setExternals(new_externals);
            }
        }}>
            Back
        </Button>
    ];

    return <>
        <AntdModal className="cf-modal" title={modalTitle === 'Edit Flag' ? 'Edit Flag' : 'Flags'} visible={true} onOk={() => {
            setView('');
        }} onCancel={() => {
            setView('');
        }}
            footer={modalTitle === 'Edit Flag' ? edit_flag_footer : flag_manager_footer}
        >
            {
                modalTitle !== 'Edit Flag' ? (
                    <div>
                        {externals?.flags?.length > 0 ?
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 8,
                                fontSize: 13,
                                color: 'var(--ed-text-soft)'
                            }}>
                                <input type='checkbox'
                                    checked={selected.size === externals.flags.length}
                                    onChange={() => {
                                        setSelected(selected.size === externals.flags.length
                                            ? new Set() : new Set(externals.flags.map((_, i) => i)));
                                    }}
                                    style={{
                                        width: 15,
                                        height: 15,
                                        accentColor: 'var(--ed-accent)',
                                        cursor: 'pointer'
                                    }} />
                                Select all
                                {selected.size > 0 ? <span>— {selected.size} selected</span> : <></>}
                            </div> : <></>}
                        {flags_overview}
                    </div>
                ) : (
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '12px 14px',
                            border: '1px solid var(--ed-border)',
                            borderRadius: 8,
                            background: 'var(--ed-surface-2)',
                            marginBottom: 16
                        }}>
                            <span style={{
                                flex: '0 0 auto',
                                fontWeight: 500,
                                color: 'var(--ed-text-soft)'
                            }}>Flag Name</span>
                            <input className='panel-input' value={externals?.flags[isEditingFlag]?.name} style={{
                                flex: '1 1 auto',
                                minWidth: 0,
                                fontWeight: 500,
                                color: 'var(--ed-text)'
                            }} onChange={(e) => {
                                let new_externals = { ...externals };
                                new_externals.flags[isEditingFlag].name = e.target.value;
                                setExternals(new_externals);
                            }} />
                        </div>

                        <p style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '.04em',
                            color: '#9aa4af',
                            margin: '0 0 8px'
                        }}>States</p>
                        {flag_overview}
                    </div>
                )
            }
        </AntdModal>
    </>;

}
